import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    adminGetWelcomeConfig,
    adminSetWelcomeConfig,
    adminListPromos,
} from "../../../Admin/adminApi";

/* helpers */
function Toggle({ checked, onChange, label }) {
    return (
        <label className="toggle">
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span className="tknob" />
            {label ? <span className="tlabel">{label}</span> : null}
        </label>
    );
}

const EMPTY_STEP = { enabled: false, day: "", email: false, push: false };

function normalizeStepsFromCfg(cfgSteps) {
    const base = Array.from({ length: 5 }, () => ({ ...EMPTY_STEP }));

    if (!Array.isArray(cfgSteps)) return base;

    const cleaned = cfgSteps
        .filter((s) => s && Number.isFinite(Number(s.day)))
        .map((s) => ({
            enabled: true,
            day: String(Number(s.day)),
            email: !!s.email,
            push: !!s.push,
        }))
        .slice(0, 5);

    cleaned.forEach((s, idx) => {
        base[idx] = s;
    });

    return base;
}

function buildPayloadSteps(steps) {
    if (!Array.isArray(steps)) return [];
    return steps
        .filter((s) => s?.enabled && s.day !== "" && Number.isFinite(Number(s.day)))
        .map((s) => ({
            day: Number(s.day),
            email: !!s.email,
            push: !!s.push,
        }));
}

export default function PromoWelcomeConfig() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const [promos, setPromos] = useState([]);
    const [active, setActive] = useState(true);
    const [promoId, setPromoId] = useState("");

    const [validityDays, setValidityDays] = useState(7);
    const [steps, setSteps] = useState(() =>
        Array.from({ length: 5 }, () => ({ ...EMPTY_STEP }))
    );

    const eligibleWelcomePromos = useMemo(() => {
        return Array.isArray(promos)
            ? promos.filter(
                (p) => (p.kind === "percent" || p.kind === "fixed") && !p.is_referral
            )
            : [];
    }, [promos]);

    const selectedPromo = useMemo(() => {
        return eligibleWelcomePromos.find((p) => String(p.id) === String(promoId)) || null;
    }, [eligibleWelcomePromos, promoId]);

    const activeStepsCount = useMemo(
        () => steps.filter((s) => s.enabled && s.day !== "").length,
        [steps]
    );

    async function load() {
        setLoading(true);
        setErr(null);
        try {
            const [cfg, list] = await Promise.all([
                adminGetWelcomeConfig(),
                adminListPromos(),
            ]);

            setPromos(Array.isArray(list) ? list : []);

            setActive(!!cfg?.active);
            setPromoId(cfg?.welcome_promo_id != null ? String(cfg.welcome_promo_id) : "");

            const vd = Number(cfg?.validity_days);
            setValidityDays(Number.isFinite(vd) && vd > 0 ? vd : 7);

            setSteps(normalizeStepsFromCfg(cfg?.contact_plan));
        } catch (e) {
            setErr(e?.message || "Errore caricamento Welcome config");
        } finally {
            setLoading(false);
        }
    }

    async function save(nextActive = active, nextPromoId = promoId) {
        setSaving(true);
        setErr(null);
        try {
            const vd = Number(validityDays);
            const safeValidity =
                Number.isFinite(vd) && vd > 0 ? Math.trunc(vd) : 7;

            await adminSetWelcomeConfig({
                active: !!nextActive,
                welcome_promo_id: nextPromoId ? Number(nextPromoId) : null,
                validity_days: safeValidity,
                contact_plan: buildPayloadSteps(steps),
            });

            toast.success("Welcome config salvata");
            await load();
        } catch (e) {
            setErr(e?.message || "Errore salvataggio Welcome config");
            toast.error(e?.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    function updateStep(i, patch) {
        setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
    }

    return (
        <div className={`group-card group-card--lift ${!active ? "welcome-off" : ""}`}>
            <div className="group-head">
                <div className="gh-left plain">
                    <span className="gh-title">Configurazione promo Welcome</span>
                </div>
                <div className="gh-right">
                    <button
                        type="button"
                        className="btn-lite"
                        onClick={load}
                        disabled={loading || saving}
                    >
                        Ricarica
                    </button>
                </div>
            </div>

            <div className="group-body">
                {err ? <div className="promo-empty">{err}</div> : null}

                {loading ? (
                    <div className="promo-empty">Caricamento…</div>
                ) : (
                    <>
                        <section className="promo-config-hero">
                            <div className="promo-config-hero-copy">
                                <span className={`promo-status-pill ${active ? "is-on" : "is-off"}`}>
                                    {active ? "Welcome attiva" : "Welcome disattiva"}
                                </span>
                                <h2 className="promo-config-title">Onboarding nuovi utenti</h2>
                                <p className="promo-config-text">
                                    Qui scegli la promo base di benvenuto, per quanti giorni vale e
                                    in quali giorni contattare l’utente dopo la verifica email.
                                </p>
                            </div>

                            <div className="promo-config-stats">
                                <div className="promo-config-stat">
                                    <span className="promo-config-stat-label">Promo scelta</span>
                                    <strong className="promo-config-stat-value">
                                        {selectedPromo?.name || "Nessuna"}
                                    </strong>
                                </div>
                                <div className="promo-config-stat">
                                    <span className="promo-config-stat-label">Validità</span>
                                    <strong className="promo-config-stat-value">
                                        {validityDays} giorni
                                    </strong>
                                </div>
                                <div className="promo-config-stat">
                                    <span className="promo-config-stat-label">Contatti attivi</span>
                                    <strong className="promo-config-stat-value">
                                        {activeStepsCount}
                                    </strong>
                                </div>
                            </div>
                        </section>

                        <div className="welcome-top">
                            <div className="welcome-block">
                                <div className="welcome-block-title">Stato</div>

                                <label className="welcome-row">
                                    <span className="welcome-row-label">Attivo</span>
                                    <Toggle
                                        checked={active}
                                        onChange={(v) => {
                                            setActive(v);
                                            save(v, promoId);
                                        }}
                                    />
                                </label>

                                <div className="welcome-subhint muted">
                                    Se disattivo, nessuna promo “welcome” viene applicata.
                                </div>
                            </div>

                            <div className="welcome-block">
                                <div className="welcome-block-title">Promo & validità</div>

                                <label className="welcome-field">
                                    <span className="welcome-field-label">Promo base (plain)</span>
                                    <select value={promoId} onChange={(e) => setPromoId(e.target.value)}>
                                        <option value="">— Nessuna —</option>
                                        {eligibleWelcomePromos.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                #{p.id} {p.name || "—"} {p.code ? `· ${p.code}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <div className="welcome-validity-row">
                                    <span className="welcome-validity-label">Valida per</span>

                                    <input
                                        className="welcome-validity-input"
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={validityDays}
                                        onChange={(e) => setValidityDays(e.target.value)}
                                    />

                                    <span className="welcome-validity-suffix">
                                        giorni dalla verifica email
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="divider" />

                        <div className="panel panel--soft">
                            <div className="panel-head">
                                <div className="panel-title">Reminder Automatico Scadenza Promo</div>
                            </div>

                            <div className="panel-body">
                                <div className="welcome-steps">
                                    {steps.map((s, idx) => (
                                        <div
                                            key={idx}
                                            className={`welcome-step ${s.enabled ? "is-on" : "is-off"}`}
                                        >
                                            <label className="welcome-step-top">
                                                <input
                                                    type="checkbox"
                                                    checked={!!s.enabled}
                                                    onChange={(e) =>
                                                        updateStep(idx, { enabled: e.target.checked })
                                                    }
                                                />
                                                <span className="welcome-step-label">Giorno</span>
                                                <input
                                                    className="welcome-step-day"
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    placeholder={`${idx + 1}`}
                                                    value={s.day}
                                                    disabled={!s.enabled}
                                                    onChange={(e) => updateStep(idx, { day: e.target.value })}
                                                />
                                            </label>

                                            <div className="welcome-step-channels">
                                                <label className="welcome-chk">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!s.email}
                                                        disabled={!s.enabled}
                                                        onChange={(e) =>
                                                            updateStep(idx, { email: e.target.checked })
                                                        }
                                                    />
                                                    <span>Email</span>
                                                </label>

                                                <label className="welcome-chk">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!s.push}
                                                        disabled={!s.enabled}
                                                        onChange={(e) =>
                                                            updateStep(idx, { push: e.target.checked })
                                                        }
                                                    />
                                                    <span>Notifica</span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <p className="promo-hint">
                                    I giorni sono relativi alla data di verifica email, cioè quando
                                    l’utente conferma l’indirizzo.
                                </p>
                            </div>
                        </div>

                        <div className="promo-actions-right">
                            <button
                                type="button"
                                className="btn-cta"
                                onClick={() => save(active, promoId)}
                                disabled={saving}
                                title="Salva"
                            >
                                💾 Salva
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
